import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Download, Plus, Trash2, FileText, IndianRupee, Loader2, CheckCircle2, Mail } from 'lucide-react';

const API = getApiUrl();
const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmtINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const now = new Date();
const YEARS = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

async function downloadPdf(payslipId, label) {
  try {
    const res = await axios.get(`${API}/api/payroll/my/payslip/${payslipId}/pdf`, { withCredentials: true, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${label}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(e?.response?.status === 403 ? 'Payslip not available yet' : 'Download failed');
  }
}

// ============================= EMPLOYEE VIEW =============================
const EmployeeView = () => {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/api/payroll/my/available`, { withCredentials: true })
      .then((r) => setSlips(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <div className="max-w-3xl mx-auto" data-testid="employee-payslip-view">
      <h1 className="text-3xl font-bold text-slate-800 mb-1">My Payslips</h1>
      <p className="text-sm text-slate-500 mb-6">Payslips are available from the 5th of the following month.</p>
      {loading ? <Loader2 className="animate-spin text-teal-600" /> :
        slips.length === 0 ? (
          <Card className="p-10 text-center text-slate-500" data-testid="no-payslips">
            <FileText className="mx-auto mb-3 text-slate-300" size={40} />
            No payslips available yet. Your latest month's payslip will appear here after the 5th.
          </Card>
        ) : (
          <div className="space-y-3">
            {slips.map((s) => (
              <Card key={s.payslip_id} className="p-4 flex items-center justify-between" data-testid={`payslip-row-${s.year}-${s.month}`}>
                <div>
                  <div className="font-semibold text-slate-800">{MONTHS[s.month]} {s.year}</div>
                  <div className="text-sm text-slate-500">Net Pay: {fmtINR(s.net_pay)}</div>
                </div>
                <Button onClick={() => downloadPdf(s.payslip_id, `Payslip_${MONTHS[s.month]}_${s.year}`)}
                  className="bg-teal-600 hover:bg-teal-700" data-testid={`download-${s.year}-${s.month}`}>
                  <Download size={16} className="mr-2" /> Download
                </Button>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
};

// ====================== SALARY STRUCTURE DIALOG ======================
const SalaryDialog = ({ emp, onClose, onSaved }) => {
  const [gross, setGross] = useState('');
  const [profile, setProfile] = useState({ designation: '', department: '', date_of_joining: '', pan: '', bank_account_last4: '', location: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setGross(emp.gross_monthly || '');
    setProfile({
      designation: emp.designation || '', department: emp.department || '', date_of_joining: emp.date_of_joining || '',
      pan: emp.pan || '', bank_account_last4: emp.bank_account_last4 || '', location: emp.location || '',
    });
  }, [emp]);
  const save = async () => {
    if (!gross || Number(gross) <= 0) { toast.error('Enter a valid gross salary'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/payroll/employees/${emp.user_id}/structure`, { gross_monthly: Number(gross) }, { withCredentials: true });
      await axios.put(`${API}/api/payroll/employees/${emp.user_id}/profile`, profile, { withCredentials: true });
      toast.success('Salary structure saved'); onSaved();
    } catch (e) { toast.error('Save failed'); } finally { setSaving(false); }
  };
  const g = Number(gross) || 0;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="salary-dialog">
        <DialogHeader><DialogTitle>Salary Setup — {emp.full_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Gross Monthly Salary (₹)</Label>
            <Input type="number" value={gross} onChange={(e) => setGross(e.target.value)} data-testid="gross-input" placeholder="30000" />
            {g > 0 && (
              <div className="mt-2 text-xs text-slate-500 bg-slate-50 rounded p-2" data-testid="split-preview">
                Basic (50%): {fmtINR(g * 0.5)} · HRA (25%): {fmtINR(g * 0.25)} · Special (25%): {fmtINR(g * 0.25)}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[['designation', 'Designation'], ['department', 'Department'], ['date_of_joining', 'Date of Joining (YYYY-MM-DD)'], ['location', 'Location'], ['pan', 'PAN'], ['bank_account_last4', 'Bank A/C (last 4)']].map(([k, lbl]) => (
              <div key={k}>
                <Label className="text-xs">{lbl}</Label>
                <Input value={profile[k]} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} data-testid={`profile-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-structure-btn">
            {saving && <Loader2 className="animate-spin mr-2" size={16} />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ====================== PAYSLIP EDIT DIALOG ======================
const PayslipDialog = ({ payslipId, onClose, onChanged }) => {
  const [ps, setPs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/api/payroll/payslips/${payslipId}`, { withCredentials: true }).then((r) => setPs(r.data));
  }, [payslipId]);
  useEffect(() => { load(); }, [load]);
  if (!ps) return null;
  const setLine = (type, i, field, val) => {
    const arr = [...ps[type]]; arr[i] = { ...arr[i], [field]: field === 'amount' ? val : val }; setPs({ ...ps, [type]: arr });
  };
  const addLine = (type) => setPs({ ...ps, [type]: [...ps[type], { key: `custom_${Date.now()}`, label: '', amount: 0 }] });
  const removeLine = (type, i) => setPs({ ...ps, [type]: ps[type].filter((_, j) => j !== i) });
  const gross = ps.earnings.reduce((s, e) => s + Number(e.amount || 0), 0);
  const ded = ps.deductions.reduce((s, d) => s + Number(d.amount || 0), 0);
  const save = async (publish) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/payroll/payslips/${payslipId}`, {
        earnings: ps.earnings.map((e) => ({ key: e.key, label: e.label, amount: Number(e.amount || 0) })),
        deductions: ps.deductions.map((d) => ({ key: d.key, label: d.label, amount: Number(d.amount || 0) })),
        remarks: ps.remarks || null,
      }, { withCredentials: true });
      if (publish) await axios.post(`${API}/api/payroll/payslips/${payslipId}/publish`, {}, { withCredentials: true });
      toast.success(publish ? 'Payslip published' : 'Saved'); onChanged(); if (publish) onClose(); else load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  const emailIt = async () => {
    setEmailing(true);
    try {
      await axios.post(`${API}/api/payroll/payslips/${payslipId}/email`, {}, { withCredentials: true });
      toast.success('Payslip emailed to employee');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Email failed'); } finally { setEmailing(false); }
  };
  const Section = ({ type, title }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-semibold text-sm text-slate-700">{title}</h4>
        <Button size="sm" variant="ghost" onClick={() => addLine(type)} data-testid={`add-${type}`}><Plus size={14} /> Add</Button>
      </div>
      {ps[type].map((line, i) => (
        <div key={i} className="flex gap-2 mb-1.5">
          <Input value={line.label} placeholder="Label" onChange={(e) => setLine(type, i, 'label', e.target.value)} className="flex-1" data-testid={`${type}-label-${i}`} />
          <Input type="number" value={line.amount} onChange={(e) => setLine(type, i, 'amount', e.target.value)} className="w-32" data-testid={`${type}-amount-${i}`} />
          <Button size="icon" variant="ghost" onClick={() => removeLine(type, i)} data-testid={`${type}-remove-${i}`}><Trash2 size={14} className="text-red-500" /></Button>
        </div>
      ))}
    </div>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="payslip-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {ps.employee_snapshot?.full_name} — {MONTHS[ps.month]} {ps.year}
            <Badge variant={ps.status === 'published' ? 'default' : 'secondary'}>{ps.status}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-5">
          <Section type="earnings" title="Earnings (add Bonus / Incentive here)" />
          <Section type="deductions" title="Deductions (add TDS etc. here)" />
        </div>
        <div className="flex justify-between text-sm bg-slate-50 rounded p-3 mt-2" data-testid="payslip-totals">
          <span>Gross: <b>{fmtINR(gross)}</b></span>
          <span>Deductions: <b>{fmtINR(ded)}</b></span>
          <span className="text-teal-700">Net Pay: <b>{fmtINR(gross - ded)}</b></span>
        </div>
        <div>
          <Label className="text-xs">Remarks (optional)</Label>
          <Input value={ps.remarks || ''} onChange={(e) => setPs({ ...ps, remarks: e.target.value })} data-testid="remarks-input" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => downloadPdf(payslipId, `Payslip_${MONTHS[ps.month]}_${ps.year}`)} data-testid="download-in-dialog"><Download size={16} className="mr-1" /> PDF</Button>
          {ps.status === 'published' && (
            <Button variant="outline" onClick={emailIt} disabled={emailing} data-testid="email-payslip-btn">
              {emailing ? <Loader2 className="animate-spin mr-1" size={16} /> : <Mail size={16} className="mr-1" />} Email to Employee
            </Button>
          )}
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn">Save</Button>
          <Button onClick={() => save(true)} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="publish-btn">
            {saving && <Loader2 className="animate-spin mr-2" size={16} />}<CheckCircle2 size={16} className="mr-1" /> Save & Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================= ADMIN VIEW =============================
const AdminView = () => {
  const [tab, setTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [year, setYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [salaryEmp, setSalaryEmp] = useState(null);
  const [editPid, setEditPid] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadEmployees = useCallback(() => {
    axios.get(`${API}/api/payroll/employees`, { withCredentials: true }).then((r) => setEmployees(r.data || []));
  }, []);
  const loadPayslips = useCallback(() => {
    axios.get(`${API}/api/payroll/payslips?month=${month}&year=${year}`, { withCredentials: true }).then((r) => setPayslips(r.data || []));
  }, [month, year]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { if (tab === 'run') loadPayslips(); }, [tab, loadPayslips]);

  const backfill = async () => {
    const r = await axios.post(`${API}/api/payroll/backfill-employee-ids`, {}, { withCredentials: true });
    toast.success(`Assigned ${r.data.assigned} Employee ID(s)`); loadEmployees();
  };
  const generate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/payroll/generate`, { month, year }, { withCredentials: true });
      toast.success(`Generated ${r.data.generated} payslip(s)`); loadPayslips();
    } catch (e) { toast.error('Generation failed. Ensure salary structures are set.'); } finally { setBusy(false); }
  };
  const emailMonth = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/payroll/email-month`, { month, year }, { withCredentials: true });
      toast.success(`Emailed ${r.data.sent} payslip(s)` + (r.data.skipped?.length ? `, ${r.data.skipped.length} skipped` : ''));
    } catch (e) { toast.error('Email failed'); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-payroll-view">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Payroll</h1>
          <p className="text-sm text-slate-500">Manage salaries and generate payslips.</p>
        </div>
        <Button variant="outline" onClick={backfill} data-testid="backfill-btn">Assign missing Employee IDs</Button>
      </div>
      <div className="flex gap-2 mb-5 border-b">
        {[['employees', 'Employees & Salary'], ['run', 'Run Payroll']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500'}`}>{l}</button>
        ))}
      </div>

      {tab === 'employees' && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left p-3">Emp ID</th><th className="text-left p-3">Name</th><th className="text-left p-3">Role</th><th className="text-right p-3">Gross / mo</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.user_id} className="border-t" data-testid={`emp-row-${e.user_id}`}>
                  <td className="p-3 font-mono text-xs">{e.employee_id || '—'}</td>
                  <td className="p-3 font-medium text-slate-800">{e.full_name}</td>
                  <td className="p-3"><Badge variant="secondary">{e.role}</Badge></td>
                  <td className="p-3 text-right">{e.gross_monthly ? fmtINR(e.gross_monthly) : <span className="text-slate-400">not set</span>}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => setSalaryEmp(e)} data-testid={`set-salary-${e.user_id}`}>{e.has_structure ? 'Edit' : 'Set'} Salary</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'run' && (
        <div>
          <div className="flex items-end gap-3 mb-4 flex-wrap">
            <div><Label className="text-xs">Month</Label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="block border rounded px-3 py-2 text-sm" data-testid="month-select">
                {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Year</Label>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="block border rounded px-3 py-2 text-sm" data-testid="year-select">
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <Button onClick={generate} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="generate-btn">
              {busy && <Loader2 className="animate-spin mr-2" size={16} />}Generate Payslips
            </Button>
            <Button onClick={emailMonth} disabled={busy} variant="outline" data-testid="email-month-btn">
              <Mail size={16} className="mr-2" /> Email Published
            </Button>
          </div>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr><th className="text-left p-3">Emp ID</th><th className="text-left p-3">Name</th><th className="text-right p-3">Gross</th><th className="text-right p-3">Net</th><th className="text-center p-3">Status</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {payslips.length === 0 ? <tr><td colSpan="6" className="p-8 text-center text-slate-400">No payslips for {MONTHS[month]} {year}. Click "Generate Payslips".</td></tr> :
                  payslips.map((p) => (
                    <tr key={p.payslip_id} className="border-t" data-testid={`slip-row-${p.payslip_id}`}>
                      <td className="p-3 font-mono text-xs">{p.employee_id || '—'}</td>
                      <td className="p-3 font-medium text-slate-800">{p.employee_snapshot?.full_name}</td>
                      <td className="p-3 text-right">{fmtINR(p.gross_earnings)}</td>
                      <td className="p-3 text-right font-semibold text-teal-700">{fmtINR(p.net_pay)}</td>
                      <td className="p-3 text-center"><Badge variant={p.status === 'published' ? 'default' : 'secondary'}>{p.status}</Badge></td>
                      <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => setEditPid(p.payslip_id)} data-testid={`edit-slip-${p.payslip_id}`}>Edit / Publish</Button></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {salaryEmp && <SalaryDialog emp={salaryEmp} onClose={() => setSalaryEmp(null)} onSaved={() => { setSalaryEmp(null); loadEmployees(); }} />}
      {editPid && <PayslipDialog payslipId={editPid} onClose={() => setEditPid(null)} onChanged={loadPayslips} />}
    </div>
  );
};

export default function PayrollPage({ currentUser }) {
  const { user } = useAuth();
  const role = (currentUser || user)?.role;
  return <div className="p-6">{role === 'super_admin' ? <AdminView /> : <EmployeeView />}</div>;
}
